import { JSX, FC, Key, Dispatch, SetStateAction, ReactNode } from "react";
import { APP_PATHES } from "constants/appPathes";
import { NavigateFunction, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { AppStateType } from "redux/reducers/mainReducer";
import { SorterResult, TablePaginationConfig, TableRowSelection } from "antd/es/table/interface";
import { ProductType } from "products/types";
import { Dropdown, MenuProps } from "antd";
import ProductsTableStub from "components/Catalog/ProductsTableStub/ProductsTableStub";
import CatalogTable from "components/Catalog/CatalogTable/CatalogTable";
import { ReactComponent as MenuIcon } from "assets/icons/three_dots_icon.svg";

interface IUploadedProductsTableBlockProps {
  selectedKeys: Key[];
  selectedRows: ProductType[];
  setActiveTab: Dispatch<SetStateAction<string>>;
  setSorterInfo: Dispatch<SetStateAction<SorterResult<any> | SorterResult<any>[]>>;
  setSelectedKeys: Dispatch<SetStateAction<React.Key[]>>;
  setSelectedRows: Dispatch<SetStateAction<ProductType[]>>;
  setPaginationInfo: Dispatch<SetStateAction<TablePaginationConfig>>;
  showDeleteModal: (values: ProductType[], text: string) => void;
  showCreateCopyModal: () => void;
  isAllProductsSelected: boolean;
  setExcludedProductIds: Dispatch<SetStateAction<number[]>>;
  setIsSingleProductDelete: Dispatch<SetStateAction<boolean>>;
  setIsAllProductsSelected: Dispatch<SetStateAction<boolean>>;
  setCountProductsForDelete: Dispatch<SetStateAction<number>>;
}

const selectionOptions: { key: string, text: string, isAllSelected: boolean }[] = [
  { key: "all-data", text: "Выбрать все", isAllSelected: true },
  { key: "all-page", text: "Выбрать все на странице", isAllSelected: false },
  { key: "none", text: "Очистить выбор", isAllSelected: false },
];

const UploadedProductsTableBlock: FC<IUploadedProductsTableBlockProps> = ({
  selectedKeys,
  selectedRows,
  setActiveTab,
  setSorterInfo,
  setSelectedKeys,
  setSelectedRows,
  setPaginationInfo,
  showDeleteModal,
  isAllProductsSelected,
  setExcludedProductIds,
  setIsSingleProductDelete,
  setIsAllProductsSelected,
  setCountProductsForDelete,
}): JSX.Element => {
  const navigate: NavigateFunction = useNavigate();

  const { list, count, isFetching } = useSelector((state: AppStateType) => state.productsSlice.products);

  const renderProductActions = (value: ProductType, idx: number): ReactNode => {
    const onEditHandler = (): void => {
      navigate(`${APP_PATHES.IMPORT_MASTER_MANUAL}/${value.id}`);
    };

    const items: MenuProps["items"] = [
      {
        label: <a>Редактировать</a>,
        key: `edit-${idx}`,
        onClick: onEditHandler,
      },
      // TODO пока скрываем
      // {
      //   label: <a>Создать копию</a>,
      //   key: `copy-${idx}`,
      //   onClick: showCreateCopyModal,
      //   disabled: true
      // },
      // {
      //   label: <a>Редактировать цену</a>,
      //   key: `edit-price-${idx}`,
      //   disabled: true
      // },
      {
        label: <a style={{ color: "#FF4D4F" }}>Удалить</a>,
        key: `delete-${idx}`,
        onClick: () => {
          setIsSingleProductDelete(true);
          showDeleteModal([value], "Товар будет удален без возможности восстановления");
        },
      },
    ];

    return (
      <Dropdown menu={{ items }} trigger={["click"]}>
        <MenuIcon className="m-auto cursor-pointer secondary-color" />
      </Dropdown>
    );
  };

  // Логика работы с чекбоксами у таблицы
  const handleSelection = (isAllSelected: boolean, keys: Key[] | null, rows: ProductType[], count: number): void => {
    setIsAllProductsSelected(isAllSelected);
    setSelectedKeys(keys);
    setSelectedRows(rows);
    setCountProductsForDelete(count);
  };

  const rowSelection: TableRowSelection<any> = {
    selections: selectionOptions.map(({ key, text, isAllSelected }) => ({
      key,
      text,
      onSelect: (selectedRowKeys: Key[]): void => {
        const keys: Key[] = key === "none" ? null : selectedRowKeys;
        const rows: ProductType[] = key === "all-page"
          ? list.map((product, idx) => ({ key: idx.toString(), ...product }))
          : [];

        const productsCount: Record<string, number> = {
          "all-data": count,
          "all-page": selectedRowKeys.length,
          "none": 0
        };

        if (key !== "all-data" || !isAllProductsSelected) {
          setExcludedProductIds([]);
          handleSelection(isAllSelected, keys, rows, productsCount[key]);
        }
      }
    })),
    selectedRowKeys: selectedKeys,
    onChange: (selectedRowKeys: Key[], rows: ProductType[]) => {
      if (isAllProductsSelected) {
        const getRowIdDifference = (
          sourceRows: ProductType[] | undefined,
          targetRows: ProductType[]
        ): number[] => {
          return sourceRows
            ?.filter((row: ProductType) => !targetRows.some((targetRow: ProductType) => targetRow.id === row.id))
            .map((row: ProductType) => row.id);
        };

        const deselectedRowIds: number[] = getRowIdDifference(selectedRows, rows); // снятые галочки
        const reselectedRowIds: number[] = getRowIdDifference(rows, selectedRows); // возвращенные галочки

        setExcludedProductIds((prev: number[]): number[] => {
          const updatedIds: Set<number> = new Set(prev);

          deselectedRowIds?.forEach((id: number) => updatedIds.add(id));

          reselectedRowIds?.forEach((id: number) => updatedIds.delete(id));

          return Array.from(updatedIds);
        });
      }

      handleSelection(
        isAllProductsSelected,
        selectedRowKeys,
        rows,
        isAllProductsSelected ? count : rows?.length
      );
    },
  };

  return (
    <CatalogTable
      columns={[
        "name",
        "category",
        "bk_category",
        "price",
        "status",
        "actions"
      ]}
      list={list}
      count={count}
      isFetching={isFetching}
      isShowEmptyBlock={!isFetching}
      rowSelection={rowSelection}
      emptyBlock={<ProductsTableStub setActiveTab={setActiveTab} />}
      renderActions={renderProductActions}
      setSorterInfo={setSorterInfo}
      setPaginationInfo={setPaginationInfo}
    />
  );
};

export default UploadedProductsTableBlock;
